import React, { useCallback } from 'react';
import { FileTypeEnum, GetGenericListCommandDto } from 'api/actor';
import {
  DocumentScopeEnum,
  DocumentViewItem,
  GetDocumentViewItemsResponse
} from 'api/documents';
import { FileText, Eye, Shield } from 'lucide-react';
import { documentsAPI } from 'services/swagger-api';
import ServerTable from 'shared/contexts/table-context/components/ServerTable';
import { FetchDataParams, TableResponse } from 'slices/generic-table/genericTableSlice';
import { fileTypeLabels } from 'features/document-view/DocumentsView';

interface PersonAdministrativeDocumentsProps {
  personId: string;
}

const statusColors: Record<string, string> = {
  PENDING_VALIDATION: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  VALIDATED: 'bg-green-100 text-green-800 border-green-200',
  REJECTED: 'bg-red-100 text-red-800 border-red-200',
  EXPIRED: 'bg-orange-100 text-orange-800 border-orange-200',
  ARCHIVED: 'bg-gray-100 text-gray-800 border-gray-200'
};

const formatDate = (date: Date | string) => {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const PersonAdministrativeDocuments: React.FC<PersonAdministrativeDocumentsProps> = ({
  personId
}) => {
  const fetchDocuments = useCallback(
    async (params: FetchDataParams): Promise<TableResponse<DocumentViewItem>> => {
      try {
        const command: GetGenericListCommandDto = {
          pageNumber: params.pageNumber,
          pageSize: params.pageSize,
          searchString: params.searchString || undefined,
          sorting: params.sorting as any,
          filters: {
            personIds: [personId],
            scopes: [DocumentScopeEnum.Subscriber]
          }
        };

        const result: GetDocumentViewItemsResponse =
          await documentsAPI.getDocuments({
            getGenericListCommandDto: command
          });

        return {
          items: result.items,
          totalItems: result.totalCount,
          totalPages: Math.ceil(result.totalCount / params.pageSize),
          currentPage: params.pageNumber
        };
      } catch (error) {
        console.error('Erreur lors de la récupération des documents:', error);
        throw error;
      }
    },
    [personId]
  );

  const handleView = (doc: DocumentViewItem) => {
    console.log('Voir:', doc.name);
  };

  return (
    <ServerTable<DocumentViewItem>
      apiConfig={{
        fetchData: fetchDocuments
      }}
      columns={[
        {
          key: 'name',
          label: 'Nom du document',
          sortable: true,
          width: '300px',
          render: (value: any, row: DocumentViewItem) => (
            <div className="flex items-center gap-3">
              <FileText className="text-red-500" size={20} />
              <div className="flex flex-col">
                <span className="font-medium text-gray-900 truncate max-w-xs">
                  {String(value)}
                </span>
                <span className="text-xs text-gray-500">{row.mimetype}</span>
              </div>
            </div>
          )
        },
        {
          key: 'fileType',
          label: 'Type de fichier',
          sortable: true,
          render: (value: any) => (
            <div className="flex items-center gap-2">
              <Shield className="text-primary" size={16} />
              <span className="text-sm font-medium text-gray-700">
                {fileTypeLabels[value as FileTypeEnum]}
              </span>
            </div>
          )
        },
        {
          key: 'status',
          label: 'Statut',
          sortable: true,
          align: 'center',
          render: (value: any, row: DocumentViewItem) => (
            <div className="flex flex-col items-center gap-1">
              <span
                className={`px-3 py-1 rounded-full text-xs font-medium border ${
                  statusColors[value as string] ||
                  'bg-gray-100 text-gray-800 border-gray-200'
                }`}
              >
                {String(value)}
              </span>
              {row.validationDate && (
                <span className="text-xs text-gray-500">
                  Validé le {formatDate(row.validationDate)}
                </span>
              )}
            </div>
          )
        },
        {
          key: 'uploadDate',
          label: "Date d'upload",
          sortable: true,
          render: (value: any) => (
            <div className="flex flex-col">
              <span className="text-sm text-gray-900">
                {formatDate(value as Date)}
              </span>
              <span className="text-xs text-gray-500">
                {new Date(value as string).toLocaleTimeString('fr-FR', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </div>
          )
        }
      ]}
      actions={{
        row: [
          {
            icon: <Eye size={16} />,
            onClick: handleView,
            variant: 'primary'
          }
        ]
      }}
      onRowClick={handleView}
    />
  );
};

export default PersonAdministrativeDocuments;
